/**
 * User data and preferences stored in IndexedDB
 */

import type { BuyerProject } from "@/types/buyerProject";
import type { BuyerUnit } from "@/types/buyerUnit";
import type { SellerUnitAndProject } from "@/types/sup";

const DB_NAME = "settlin_user_db";
const DB_VERSION = 1;

// Object stores
const STORES = {
	USER_DATA: "user_data",
	USER_PREFERENCES: "user_preferences",
	DASHBOARD_DATA: "dashboard_data",
	CACHE_METADATA: "cache_metadata",
} as const;

// Store keys
const KEYS = {
	CURRENT_USER: "current_user",
	USER_PREFERENCES: "user_preferences",
	BUYER_DASHBOARD: "buyer_dashboard",
	SELLER_DASHBOARD: "seller_dashboard",
} as const;

export interface UserPreferences {
	selectedGroupId?: string;
	relationship?: "buyer" | "seller";
	lastUpdated?: number;
}

export interface CachedUser {
	id: string;
	name?: string;
	relation?: string;
	groups?: Array<{
		id: string;
		name: string;
		type: string;
		relationship: string;
		members?: Array<{
			id: string;
			name: string;
			designation?: string;
		}>;
	}>;
	emails?: Array<{
		id: string;
		address: string;
		type: string;
		verified: boolean;
	}>;
	phones?: Array<{
		id: string;
		number: string;
		type: string;
		verified: boolean;
	}>;
	profile?: {
		name?: string;
		image?: string;
		addresses?: Array<{
			type: string;
			value: string;
		}>;
		idProofs?: Array<{
			id: string;
			type: string;
		}>;
		utcOffset?: {
			offset: number;
			utc: string;
			value: string;
		};
	};
	lastUpdated: number;
	internal?: boolean;
}

export interface DashboardData {
	buyerData?: {
		properties?: BuyerUnit[];
		projects?: BuyerProject[];
		activities?: Record<string, unknown>[];
		requests?: Record<string, unknown>[];
		lastUpdated: number;
	};
	sellerData?: {
		properties?: SellerUnitAndProject[];
		projects?: Record<string, unknown>[];
		analytics?: Record<string, unknown>[];
		leads?: Record<string, unknown>[];
		lastUpdated: number;
	};
}

export interface CacheMetadata {
	key: string;
	lastUpdated: number;
	expiresAt?: number;
}

/**
 * Initialize IndexedDB with all required object stores
 */
function initDB(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		if (typeof window === "undefined") {
			reject(new Error("IndexedDB not available - running on server"));
			return;
		}

		if (!("indexedDB" in window)) {
			reject(new Error("IndexedDB not supported in this browser"));
			return;
		}

		const request = indexedDB.open(DB_NAME, DB_VERSION);

		request.onerror = () => {
			const error = request.error || new Error("Unknown IndexedDB error");
			reject(new Error(`Failed to open IndexedDB: ${error.message}`));
		};

		request.onsuccess = () => resolve(request.result);

		request.onupgradeneeded = event => {
			try {
				const db = (event.target as IDBOpenDBRequest).result;

				// Create object stores if they don't exist
				Object.values(STORES).forEach(storeName => {
					if (!db.objectStoreNames.contains(storeName)) {
						const store = db.createObjectStore(storeName);

						// Add indexes for cache metadata
						if (storeName === STORES.CACHE_METADATA) {
							store.createIndex("lastUpdated", "lastUpdated");
							store.createIndex("expiresAt", "expiresAt");
						}
					}
				});
			} catch (upgradeError) {
				reject(
					new Error(
						`Failed to upgrade IndexedDB schema: ${upgradeError instanceof Error ? upgradeError.message : "Unknown error"}`,
					),
				);
			}
		};

		request.onblocked = () => {
			reject(new Error("IndexedDB upgrade blocked - please close other tabs and try again"));
		};
	});
}

// ============================================
// USER PREFERENCES
// ============================================

/**
 * Get user preferences from IndexedDB
 */
export async function getUserPreferences(): Promise<UserPreferences> {
	if (typeof window === "undefined") return {};

	try {
		const db = await initDB();
		const transaction = db.transaction([STORES.USER_PREFERENCES], "readonly");
		const store = transaction.objectStore(STORES.USER_PREFERENCES);

		return new Promise((resolve, reject) => {
			const request = store.get(KEYS.USER_PREFERENCES);
			request.onerror = () => reject(request.error);
			request.onsuccess = () => resolve(request.result || {});
		});
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn("Failed to get user preferences from IndexedDB:", error);
		return {};
	}
}

/**
 * Set user preferences in IndexedDB
 */
export async function setUserPreferences(preferences: Partial<UserPreferences>): Promise<void> {
	if (typeof window === "undefined") return;

	try {
		const db = await initDB();
		const current = await getUserPreferences();
		const updated = {
			...current,
			...preferences,
			lastUpdated: Date.now(),
		};

		const transaction = db.transaction([STORES.USER_PREFERENCES], "readwrite");
		const store = transaction.objectStore(STORES.USER_PREFERENCES);

		return new Promise((resolve, reject) => {
			const request = store.put(updated, KEYS.USER_PREFERENCES);
			request.onerror = () => reject(request.error);
			request.onsuccess = () => resolve();
		});
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn("Failed to save user preferences to IndexedDB:", error);
	}
}

// ============================================
// USER DATA
// ============================================

/**
 * Get cached user data from IndexedDB
 */
export async function getCachedUser(): Promise<CachedUser | null> {
	if (typeof window === "undefined") return null;

	try {
		const db = await initDB();
		const transaction = db.transaction([STORES.USER_DATA], "readonly");
		const store = transaction.objectStore(STORES.USER_DATA);

		return new Promise((resolve, reject) => {
			const request = store.get(KEYS.CURRENT_USER);
			request.onerror = () => reject(request.error);
			request.onsuccess = () => resolve(request.result || null);
		});
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn("Failed to get cached user from IndexedDB:", error);
		return null;
	}
}

/**
 * Cache user data in IndexedDB
 */
export async function cacheUser(user: Omit<CachedUser, "lastUpdated">): Promise<void> {
	if (typeof window === "undefined") return;

	try {
		const db = await initDB();
		const userWithTimestamp: CachedUser = {
			...user,
			lastUpdated: Date.now(),
		};

		const transaction = db.transaction([STORES.USER_DATA], "readwrite");
		const store = transaction.objectStore(STORES.USER_DATA);

		return new Promise((resolve, reject) => {
			const request = store.put(userWithTimestamp, KEYS.CURRENT_USER);
			request.onerror = () => reject(request.error);
			request.onsuccess = () => resolve();
		});
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn("Failed to cache user in IndexedDB:", error);
	}
}

// ============================================
// DASHBOARD DATA
// ============================================

/**
 * Get cached dashboard data from IndexedDB
 */
export async function getCachedDashboardData(type: "buyer" | "seller"): Promise<DashboardData | null> {
	if (typeof window === "undefined") return null;

	try {
		const db = await initDB();
		const transaction = db.transaction([STORES.DASHBOARD_DATA], "readonly");
		const store = transaction.objectStore(STORES.DASHBOARD_DATA);

		const key = type === "buyer" ? KEYS.BUYER_DASHBOARD : KEYS.SELLER_DASHBOARD;

		return new Promise((resolve, reject) => {
			const request = store.get(key);
			request.onerror = () => reject(request.error);
			request.onsuccess = () => resolve(request.result || null);
		});
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn("Failed to get cached dashboard data from IndexedDB:", error);
		return null;
	}
}

/**
 * Cache dashboard data in IndexedDB
 */
export async function cacheDashboardData(type: "buyer" | "seller", data: DashboardData): Promise<void> {
	if (typeof window === "undefined") return;

	try {
		const db = await initDB();
		const dataWithTimestamp = {
			...data,
			lastUpdated: Date.now(),
		};

		const transaction = db.transaction([STORES.DASHBOARD_DATA], "readwrite");
		const store = transaction.objectStore(STORES.DASHBOARD_DATA);

		const key = type === "buyer" ? KEYS.BUYER_DASHBOARD : KEYS.SELLER_DASHBOARD;

		return new Promise((resolve, reject) => {
			const request = store.put(dataWithTimestamp, key);
			request.onerror = () => reject(request.error);
			request.onsuccess = () => resolve();
		});
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn("Failed to cache dashboard data in IndexedDB:", error);
	}
}

// ============================================
// CACHE MANAGEMENT
// ============================================

/**
 * Clear all cached data from IndexedDB
 */
export async function clearAllCache(): Promise<void> {
	if (typeof window === "undefined") return;

	try {
		const db = await initDB();
		const storeNames = Object.values(STORES);
		const transaction = db.transaction(storeNames, "readwrite");

		const clearPromises = storeNames.map(storeName => {
			const store = transaction.objectStore(storeName);
			return new Promise<void>((resolve, reject) => {
				const request = store.clear();
				request.onerror = () => reject(request.error);
				request.onsuccess = () => resolve();
			});
		});

		await Promise.all(clearPromises);
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn("Failed to clear all cache from IndexedDB:", error);
	}
}

/**
 * Check if cached data is still valid (within expiry time)
 */
export function isCacheValid(lastUpdated: number, maxAgeMs: number = 5 * 60 * 1000): boolean {
	return Date.now() - lastUpdated < maxAgeMs;
}

// ============================================
// GROUP PREFERENCES HELPERS
// ============================================

/**
 * Get the preferred group ID for a specific relationship
 */
export async function getPreferredGroupId(
	relationship: "buyer" | "seller",
	userGroups?: Array<{ id: string; relationship: string }>,
): Promise<string | undefined> {
	const preferences = await getUserPreferences();

	// If we have a stored preference and it matches the relationship
	if (preferences.selectedGroupId && preferences.relationship === relationship) {
		// Verify the group still exists in the user's groups
		if (userGroups?.some(group => group.id === preferences.selectedGroupId && group.relationship === relationship)) {
			return preferences.selectedGroupId;
		}
	}

	// Fall back to first group of the relationship type
	return userGroups?.find(group => group.relationship === relationship)?.id;
}

/**
 * Save the selected group and relationship preference
 */
export async function saveGroupPreference(groupId: string, relationship: "buyer" | "seller"): Promise<void> {
	// 1. Save to IndexedDB (source of truth)
	await setUserPreferences({
		selectedGroupId: groupId,
		relationship,
	});

	// 2. Sync to Zustand store and broadcast to other tabs
	if (typeof window !== "undefined") {
		// Use Promise.all to handle dynamic imports concurrently
		const [{ useUserPreferencesStore }, { broadcastSelectedGroupChange }] = await Promise.all([
			import("@/stores/user-preferences"),
			import("@/hooks/use-selected-group-sync"),
		]);

		const params = { selectedGroupId: groupId, relationship };

		// Sync to Zustand store (for current tab)
		useUserPreferencesStore.getState().setSelectedGroup(params);

		// Broadcast to other tabs
		broadcastSelectedGroupChange(params);
	}
}

/**
 * Get the effective current relationship considering cached preferences
 */
export async function getCurrentRelationship(user?: {
	relation?: string;
	groups?: Array<{ relationship: string }>;
}): Promise<"buyer" | "seller"> {
	try {
		const preferences = await getUserPreferences();

		// Use preference if available and valid
		if (preferences.relationship && (preferences.relationship === "buyer" || preferences.relationship === "seller")) {
			return preferences.relationship;
		}

		// Fall back to user relation or first group
		const firstGroup = Array.isArray(user?.groups) ? user.groups[0] : undefined;
		return (user?.relation as "buyer" | "seller") || (firstGroup?.relationship as "buyer" | "seller") || "buyer";
	} catch {
		// Fallback to user data or default
		const firstGroup = Array.isArray(user?.groups) ? user.groups[0] : undefined;
		return (user?.relation as "buyer" | "seller") || (firstGroup?.relationship as "buyer" | "seller") || "buyer";
	}
}
